import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  CreateKeyInput,
  CreateKeyResult,
  Destination,
  KeyDirection,
  KeyMode,
  KeyStatus,
} from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { Badge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";
import { CommandBlock } from "../components/CommandBlock";
import { CopyButton } from "../components/CopyButton";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState, EmptyState } from "../components/EmptyState";
import { formatRelative } from "../lib/format";
import { useMe } from "../lib/hooks";
import { OwnerCell } from "../components/OwnerCell";

const DIRECTION_LABEL: Record<KeyDirection, string> = {
  put: "Upload",
  get: "Download",
  both: "Upload + download",
};

const STATUS_TONE: Record<KeyStatus, "ok" | "warn" | "danger"> = {
  active: "ok",
  expired: "warn",
  revoked: "danger",
};

export function KeysPage() {
  const qc = useQueryClient();
  const me = useMe();
  const [creating, setCreating] = useState(false);
  // The plaintext result is held in state only long enough to show it once.
  const [revealed, setRevealed] = useState<CreateKeyResult | null>(null);

  const keys = useQuery({ queryKey: ["keys"], queryFn: () => api.keys.list() });
  const destinations = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.destinations.list(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.keys.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });

  const destinationName = (id: string) =>
    destinations.data?.find((d) => d.id === id)?.bucketOrContainer ?? "—";

  const hasDestinations = (destinations.data?.length ?? 0) > 0;

  return (
    <div>
      <PageHeader
        title="Upload keys"
        description="Issue keys scoped to a destination. The plaintext key and paste-ready command are shown only once at creation."
        action={
          <button
            className="btn-primary"
            onClick={() => setCreating(true)}
            disabled={!hasDestinations}
            title={hasDestinations ? undefined : "Add a destination first"}
          >
            New key
          </button>
        }
      />

      {keys.isLoading ? (
        <Loading />
      ) : keys.isError ? (
        <ErrorState error={keys.error} onRetry={keys.refetch} />
      ) : keys.data && keys.data.length > 0 ? (
        <Table>
          <THead>
            <tr>
              <TH>Key</TH>
              <TH>Owner</TH>
              <TH>Destination</TH>
              <TH>Direction</TH>
              <TH>Mode</TH>
              <TH>Usage</TH>
              <TH>Last used</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </tr>
          </THead>
          <TBody>
            {keys.data.map((k) => (
              <TR key={k.id}>
                <TD>
                  <div className="font-medium text-ink">
                    {k.label ?? "Untitled key"}
                  </div>
                </TD>
                <TD>
                  <OwnerCell email={k.ownerEmail} youEmail={me.data?.user.email} />
                </TD>
                <TD className="mono text-xs text-ink-muted">
                  {destinationName(k.destinationId)}
                </TD>
                <TD className="text-ink-muted">
                  {DIRECTION_LABEL[k.direction]}
                </TD>
                <TD>
                  {k.mode === "one_shot" ? (
                    <Badge tone="warn">One-shot</Badge>
                  ) : (
                    <Badge tone="neutral">Persistent</Badge>
                  )}
                </TD>
                <TD className="text-xs text-ink-muted">
                  {k.usedCount} used
                  {k.monthlyQuota != null
                    ? ` / ${k.monthlyQuota}/mo`
                    : ""}
                  {k.expiresAt
                    ? ` · expires ${formatRelative(k.expiresAt)}`
                    : ""}
                  {k.ipAllowlist?.length
                    ? ` · ${k.ipAllowlist.length} IP rule(s)`
                    : ""}
                </TD>
                <TD className="text-ink-muted">{formatRelative(k.lastUsedAt)}</TD>
                <TD>
                  <Badge tone={STATUS_TONE[k.status]}>{k.status}</Badge>
                </TD>
                <TD className="text-right">
                  <button
                    className="btn-danger"
                    disabled={k.status !== "active" || revoke.isPending}
                    onClick={() => {
                      if (confirm(`Revoke key "${k.label ?? "Untitled key"}"?`))
                        revoke.mutate(k.id);
                    }}
                  >
                    Revoke
                  </button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <EmptyState
          title={hasDestinations ? "No keys yet" : "Add a destination first"}
          description={
            hasDestinations
              ? "Create a scoped upload key and hand the one-liner to whoever needs to send files."
              : "Keys are issued against a destination. Create a destination first."
          }
          action={
            hasDestinations ? (
              <button
                className="btn-primary"
                onClick={() => setCreating(true)}
              >
                New key
              </button>
            ) : undefined
          }
        />
      )}

      <CreateKeyModal
        open={creating}
        onClose={() => setCreating(false)}
        destinations={destinations.data ?? []}
        onCreated={(result) => {
          setCreating(false);
          setRevealed(result);
        }}
      />

      <RevealKeyModal result={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}

export function CreateKeyModal({
  open,
  onClose,
  destinations,
  fixedDestinationId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  destinations: Destination[];
  fixedDestinationId?: string;
  onCreated: (result: CreateKeyResult) => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [destinationId, setDestinationId] = useState(fixedDestinationId ?? "");
  const [direction, setDirection] = useState<KeyDirection>("put");
  const [mode, setMode] = useState<KeyMode>("persistent");
  const [monthlyQuota, setMonthlyQuota] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [ipAllowlist, setIpAllowlist] = useState("");

  const create = useMutation({
    mutationFn: (input: CreateKeyInput) => api.keys.create(input),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["keys"] });
      reset();
      onCreated(result);
    },
  });

  function reset() {
    setLabel("");
    setDestinationId(fixedDestinationId ?? "");
    setDirection("put");
    setMode("persistent");
    setMonthlyQuota("");
    setExpiresAt("");
    setIpAllowlist("");
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const ips = ipAllowlist
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    create.mutate({
      destinationId: fixedDestinationId || destinationId || destinations[0]?.id || "",
      label: label || undefined,
      direction,
      mode,
      monthlyQuota: monthlyQuota ? Number(monthlyQuota) : undefined,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      ipAllowlist: ips.length ? ips : undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New upload key"
      description="Scope the key tightly — you can always issue more."
      size="lg"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="klabel">
              Label <span className="text-ink-subtle">(optional)</span>
            </label>
            <input
              id="klabel"
              className="input"
              placeholder="e.g. Acme onboarding"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          {!fixedDestinationId && (
            <div>
              <label className="label" htmlFor="kdest">
                Destination
              </label>
              <select
                id="kdest"
                className="input"
                value={destinationId}
                onChange={(e) => setDestinationId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select a destination…
                </option>
                {destinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.bucketOrContainer}
                    {d.keyPrefix ? `/${d.keyPrefix}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className="label">Direction</span>
            <div className="inline-flex overflow-hidden rounded-md border border-surface-200">
              {(["put", "get", "both"] as KeyDirection[]).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`px-3 py-1.5 text-sm font-medium ${
                    direction === d
                      ? "bg-accent text-white"
                      : "bg-surface-0 text-ink-muted hover:bg-surface-50"
                  }`}
                  onClick={() => setDirection(d)}
                >
                  {d === "put" ? "Put" : d === "get" ? "Get" : "Both"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="label">Mode</span>
            <label className="flex items-center gap-2 py-1.5 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-surface-300 text-accent focus:ring-accent/30"
                checked={mode === "one_shot"}
                onChange={(e) =>
                  setMode(e.target.checked ? "one_shot" : "persistent")
                }
              />
              One-shot (self-revokes after first use)
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="kquota">
              Monthly quota{" "}
              <span className="text-ink-subtle">(optional)</span>
            </label>
            <input
              id="kquota"
              type="number"
              min="1"
              className="input"
              placeholder="100"
              value={monthlyQuota}
              onChange={(e) => setMonthlyQuota(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="kexp">
              Expiry <span className="text-ink-subtle">(optional)</span>
            </label>
            <input
              id="kexp"
              type="datetime-local"
              className="input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="kips">
            IP allowlist <span className="text-ink-subtle">(optional)</span>
          </label>
          <input
            id="kips"
            className="input mono"
            placeholder="203.0.113.4, 198.51.100.0/24"
            value={ipAllowlist}
            onChange={(e) => setIpAllowlist(e.target.value)}
          />
          <p className="mt-1 text-xs text-ink-subtle">
            Comma- or space-separated IPs / CIDRs. Leave blank to allow any
            source.
          </p>
        </div>

        {create.isError && (
          <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {create.error instanceof Error
              ? create.error.message
              : "Could not create key."}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending}
          >
            {create.isPending ? "Creating…" : "Create key"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function RevealKeyModal({
  result,
  onClose,
}: {
  result: CreateKeyResult | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={Boolean(result)}
      onClose={onClose}
      title="Key created"
      description="Copy it now — for security we don't store the plaintext and can't show it again."
      size="lg"
    >
      {result && (
        <div className="space-y-4">
          <div className="rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
            You won't see this key again. Store it somewhere safe or paste the
            command below to whoever needs to send files.
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="label mb-0">Plaintext key</span>
              <CopyButton
                text={result.plaintext}
                className="btn px-2 py-0.5 text-xs text-accent hover:bg-accent-soft"
              />
            </div>
            <code className="mono block break-all rounded-md border border-surface-200 bg-surface-50 px-3 py-2 text-sm text-ink">
              {result.plaintext}
            </code>
          </div>

          <div>
            <span className="label">Ready-to-paste command</span>
            <CommandBlock
              unix={result.oneLiner.curl}
              windows={result.oneLiner.curlExe}
            />
          </div>

          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
